export interface ClientEnvironmentDetails {
  operatingSystem: string;
  browserDetails: string;
  browserZoomPercentage: number;
  screenResolution: string;
  browserCookieSizeInBytes: number;
}

function getOperatingSystem(lowerCaseUserAgent: string): string {
  if (lowerCaseUserAgent.includes('windows nt')) {
    return 'Windows';
  }
  if (lowerCaseUserAgent.includes('macintosh') || lowerCaseUserAgent.includes('mac os x')) {
    return 'macOS';
  }
  if (lowerCaseUserAgent.includes('android')) {
    return 'Android';
  }
  if (
    lowerCaseUserAgent.includes('iphone') ||
    lowerCaseUserAgent.includes('ipad') ||
    lowerCaseUserAgent.includes('ipod')
  ) {
    return 'iOS';
  }
  if (lowerCaseUserAgent.includes('linux')) {
    return 'Linux';
  }
  return 'unknown';
}

function getBrowserDetails(userAgent: string, lowerCaseUserAgent: string): string {
  const getBrowserVersion = (regex: RegExp) => userAgent.match(regex)?.[1] || '';

  if (lowerCaseUserAgent.includes('edg/')) {
    return `Edge ${getBrowserVersion(/Edg\/([\d.]+)/)}`.trim();
  }
  if (lowerCaseUserAgent.includes('chrome/')) {
    return `Chrome ${getBrowserVersion(/Chrome\/([\d.]+)/)}`.trim();
  }
  if (lowerCaseUserAgent.includes('firefox/')) {
    return `Firefox ${getBrowserVersion(/Firefox\/([\d.]+)/)}`.trim();
  }
  if (lowerCaseUserAgent.includes('version/') && lowerCaseUserAgent.includes('safari')) {
    return `Safari ${getBrowserVersion(/Version\/([\d.]+).*Safari/)}`.trim();
  }
  return userAgent || 'unknown';
}

function getBrowserZoomPercentage(): number {
  if (typeof window === 'undefined') {
    return 100;
  }
  const zoomLevel = window.visualViewport?.scale ?? window.devicePixelRatio ?? 1;
  return Math.round(zoomLevel * 100);
}

function getScreenResolution(): string {
  if (typeof window === 'undefined' || !window.screen) {
    return 'unknown';
  }
  return `${window.screen.width}x${window.screen.height}`;
}

function getBrowserCookieSizeInBytes(): number {
  if (typeof document === 'undefined') {
    return 0;
  }
  return document.cookie.length;
}

/**
 * Collects best-effort client environment details (OS, browser, zoom, screen
 * resolution, cookie size). Ported from one-ap-customer_web so client-side logs
 * across One AP surfaces share the same shape.
 */
export function getClientEnvironmentDetails(): ClientEnvironmentDetails {
  if (typeof navigator === 'undefined') {
    return {
      operatingSystem: 'unknown',
      browserDetails: 'unknown',
      browserZoomPercentage: 100,
      screenResolution: 'unknown',
      browserCookieSizeInBytes: 0
    };
  }

  const userAgent = navigator.userAgent || '';
  const lowerCaseUserAgent = userAgent.toLowerCase();

  return {
    operatingSystem: getOperatingSystem(lowerCaseUserAgent),
    browserDetails: getBrowserDetails(userAgent, lowerCaseUserAgent),
    browserZoomPercentage: getBrowserZoomPercentage(),
    screenResolution: getScreenResolution(),
    browserCookieSizeInBytes: getBrowserCookieSizeInBytes()
  };
}
